export interface RegisterServiceWorkerOptions {
    /** Public URL of the compiled service worker file (e.g. `/sw.js`). */
    url: string;
    /** SW scope (default: SW directory). */
    scope?: string;
    /** Called once the registration is active. */
    onReady?: (registration: ServiceWorkerRegistration) => void;
    /**
     * Called when a new worker has finished installing while another worker
     * still controls the page. The host app typically prompts the user to
     * reload and then calls {@link skipWaiting} on the returned worker.
     *
     * Fires for a worker that was already `waiting` when `register()` resolved
     * — the state a user returning after a deploy actually finds — as well as
     * for one that installs during this session, and at most once per worker.
     */
    onUpdate?: (waiting: ServiceWorker, registration: ServiceWorkerRegistration) => void;
    /** Called on registration failure. */
    onError?: (error: unknown) => void;
    /**
     * When `true`, poll the server for a fresh service worker on an interval
     * (see {@link RegisterServiceWorkerOptions.updateIntervalMs}) and, unless
     * {@link RegisterServiceWorkerOptions.reloadOnActivate} is disabled, reload
     * the page as soon as a new worker takes control. Mirrors the auto-update
     * behaviour of `vite-plugin-pwa` without depending on it. Default `false`.
     */
    autoUpdate?: boolean;
    /**
     * How often, in ms, to call `registration.update()` while `autoUpdate` is
     * on. Browsers already re-check roughly every 24h; an hourly poll keeps
     * long-lived sessions current without flooding the network. Default
     * `3600000` (1 hour).
     */
    updateIntervalMs?: number;
    /**
     * When `autoUpdate` is on, reload the page once a new worker takes control
     * (`controllerchange`), guarded against reload loops. Set to `false` to
     * keep polling but leave the reload to the host app. Default `true`.
     */
    reloadOnActivate?: boolean;
}

const DEFAULT_UPDATE_INTERVAL_MS = 60 * 60 * 1000;

/**
 * Workers already handed to `onUpdate`, so no worker is announced twice.
 *
 * Two paths reach the same waiting worker and both are needed: `updatefound`
 * catches the worker that installs while the tab is open, and the `waiting`
 * read at registration catches the one that installed on an earlier visit. In
 * the session where a deploy lands *and* the tab stays open, both fire for the
 * same worker — without this the app prompts twice.
 *
 * Keyed by worker identity rather than by registration, so a second deploy in
 * the same session still announces: that is a different `ServiceWorker` object.
 * A `WeakSet` because the entry should die with the worker it names.
 */
const announcedWorkers = new WeakSet<ServiceWorker>();

/**
 * Hand a waiting worker to `onUpdate`, at most once per worker.
 *
 * @param worker - The worker that finished installing.
 * @param registration - The registration it belongs to.
 * @param options - The caller's options, whose `onUpdate` is invoked.
 */
function announceUpdate(
    worker: ServiceWorker,
    registration: ServiceWorkerRegistration,
    options: RegisterServiceWorkerOptions,
): void {
    if (announcedWorkers.has(worker)) return;
    announcedWorkers.add(worker);
    options.onUpdate?.(worker, registration);
}

let controllerReloadWired = false;

function wireControllerReload(): void {
    if (controllerReloadWired) return;
    controllerReloadWired = true;
    let refreshing = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (refreshing) return;
        refreshing = true;
        window.location.reload();
    });
}

/**
 * Register a service worker with consistent update-detection wiring.
 *
 * Skips silently when the runtime has no `serviceWorker` support. The host
 * app keeps full control over the SW file — this helper only handles the
 * boilerplate around `register()` and update detection.
 *
 * Two things can mean "an update is ready", and only one of them is an event.
 * `updatefound` covers the worker that installs while this tab is open. The
 * commoner case has no event at all: the user visited after a deploy, the new
 * worker installed and went to `waiting`, they closed the tab, and they are back
 * now. `install` already happened, so nothing fires — which is why `waiting` is
 * read directly once the registration resolves. Both routes go through the same
 * dedupe, so a worker is announced once even when both apply.
 *
 * Neither route fires without `navigator.serviceWorker.controller`: no
 * controller means this is the first install, which is `onReady` territory, not
 * an update the user should be prompted about.
 *
 * With `autoUpdate` enabled it additionally polls `registration.update()` on
 * an interval and (by default) reloads the page when a freshly activated worker
 * takes control — a framework-agnostic equivalent of `vite-plugin-pwa`'s
 * auto-update client, implemented directly on `navigator.serviceWorker`.
 *
 * @returns The registration when it succeeds, or `null` when unsupported.
 */
export async function registerServiceWorker(
    options: RegisterServiceWorkerOptions,
): Promise<ServiceWorkerRegistration | null> {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
        return null;
    }

    try {
        const registration = await navigator.serviceWorker.register(options.url, {
            scope: options.scope,
        });

        if (registration.active) options.onReady?.(registration);

        if (registration.waiting && navigator.serviceWorker.controller) {
            announceUpdate(registration.waiting, registration, options);
        }

        registration.addEventListener("updatefound", () => {
            const installing = registration.installing;
            if (!installing) return;
            installing.addEventListener("statechange", () => {
                if (installing.state === "installed" && navigator.serviceWorker.controller) {
                    announceUpdate(installing, registration, options);
                }
            });
        });

        if (options.autoUpdate) {
            if (options.reloadOnActivate !== false) wireControllerReload();
            const intervalMs = options.updateIntervalMs ?? DEFAULT_UPDATE_INTERVAL_MS;
            window.setInterval(() => {
                void registration.update();
            }, intervalMs);
        }

        return registration;
    } catch (error) {
        options.onError?.(error);
        return null;
    }
}

/**
 * Tell a waiting worker to activate immediately. Pair with `onUpdate` to roll
 * out updates after the user confirms a reload prompt.
 */
export function skipWaiting(worker: ServiceWorker): void {
    worker.postMessage({ type: "SKIP_WAITING" });
}

/**
 * Unregister all registered service workers for this origin.
 *
 * @returns Number of workers that were unregistered.
 */
export async function unregisterAllServiceWorkers(): Promise<number> {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return 0;
    const registrations = await navigator.serviceWorker.getRegistrations();
    let count = 0;
    for (const registration of registrations) {
        const result = await registration.unregister();
        if (result) count += 1;
    }
    return count;
}
